// Dashboard do estoque (seção 7): indicadores do mês, 6 gráficos (cada um com tooltip, legenda
// quando há mais de uma série e visão em tabela) e a aba de insights. Um único filtro (mês/ano)
// no topo vale para a página inteira. Dados: backend/services/estoque/dashboard.py.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Lightbulb, PackageX } from "lucide-react";
import { getEstoqueCustosProduto, getEstoqueDashboard, getEstoqueProdutos } from "../../api";
import EstoqueLayout from "./EstoqueLayout";
import InsightsEstoque from "./InsightsEstoque";
import { AvisoSemAbertura, MESES, ProdutoSelect, formatData, useContextoEstoque } from "./comum";
import { CaixaTooltip, CardGrafico, Legenda, TabelaDados, brlCompacto, eixoProps, temaGrafico } from "./graficos";
import { AvisoErro, Campo, formatBRL, formatQtd, inputStyle, palette, useIsMobile } from "./ui";

function Kpi({ C, rotulo, valor, detalhe, alerta, Icone }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${alerta ? C.danger : C.border}`, borderRadius: 12, padding: 14, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {Icone && <Icone size={13} color={alerta ? C.danger : C.muted} />} {rotulo}
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4, color: C.text }}>{valor}</div>
      {detalhe && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{detalhe}</div>}
    </div>
  );
}

function VariacaoConsumo({ C, kpis }) {
  if (kpis.consumo_variacao_pct === null) return <span>Sem histórico para comparar</span>;
  const v = Number(kpis.consumo_variacao_pct);
  const Icone = v >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
      <Icone size={14} /> {v > 0 ? "+" : ""}{String(v).replace(".", ",")}% vs. média de {kpis.periodos_na_media} mês(es) ({formatBRL(kpis.consumo_media_3_anteriores)})
    </span>
  );
}

function GraficoCustoUnitario({ C, T }) {
  const [produtos, setProdutos] = useState([]);
  const [produtoId, setProdutoId] = useState(null);
  const [dados, setDados] = useState(null);
  useEffect(() => { getEstoqueProdutos().then(setProdutos).catch(() => {}); }, []);
  useEffect(() => {
    if (!produtoId) { setDados(null); return; }
    getEstoqueCustosProduto(produtoId).then(setDados).catch(() => setDados(null));
  }, [produtoId]);
  const pontos = (dados?.pontos || []).map((p, i) => ({ ...p, idx: i, rotulo: formatData(p.data), custo: Number(p.custo_unitario) }));

  return (
    <CardGrafico C={C} titulo="Evolução do custo unitário" subtitulo={dados ? `R$ por ${dados.unidade} · um ponto por NF` : "Escolha um produto"}
      acoes={<div style={{ width: 220, maxWidth: "45vw" }}><ProdutoSelect C={C} produtos={produtos} value={produtoId} onChange={setProdutoId} style={{ minHeight: 34, padding: "6px 8px", fontSize: 12 }} aria-label="Produto" /></div>}
      tabela={<TabelaDados C={C} linhas={pontos} colunas={[
        { id: "rotulo", rotulo: "Entrada" }, { id: "nf_numero", rotulo: "NF" }, { id: "fornecedor", rotulo: "Fornecedor" },
        { id: "quantidade", rotulo: "Qtd.", alinhar: "right", formato: formatQtd }, { id: "custo", rotulo: "Custo/un.", alinhar: "right", formato: formatBRL },
      ]} />}>
      {!produtoId || pontos.length === 0 ? (
        <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 13 }}>
          {produtoId ? "Produto sem NF lançada." : "Escolha um produto para ver o custo de cada nota."}
        </div>
      ) : (
        <ResponsiveContainer>
          <LineChart data={pontos} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={T.grid} />
            <XAxis dataKey="idx" tickFormatter={(i) => pontos[i]?.rotulo} {...eixoProps(T)} />
            <YAxis tickFormatter={(v) => formatBRL(v)} width={72} {...eixoProps(T)} domain={["auto", "auto"]} />
            <Tooltip cursor={{ stroke: T.eixo }} content={({ active, payload }) => active && payload?.length ? (
              <CaixaTooltip T={T} titulo={`NF ${payload[0].payload.nf_numero} · ${payload[0].payload.rotulo}`} linhas={[
                { rotulo: payload[0].payload.fornecedor, valor: "" },
                { rotulo: `Custo por ${dados.unidade}`, valor: formatBRL(payload[0].payload.custo), cor: T.series[0] },
                { rotulo: "Quantidade", valor: `${formatQtd(payload[0].payload.quantidade)} ${dados.unidade}` },
              ]} />) : null} />
            <Line type="linear" dataKey="custo" stroke={T.series[0]} strokeWidth={2} dot={{ r: 4, fill: T.series[0], stroke: T.superficie, strokeWidth: 2 }} activeDot={{ r: 6 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </CardGrafico>
  );
}

function VisaoGeral({ C, T, dash, isMobile }) {
  const navigate = useNavigate();
  const { kpis } = dash;
  const serie = dash.serie.map((s) => ({
    ...s, entradas: Number(s.entradas), consumo: Number(s.consumo), valor_estoque: Number(s.valor_estoque),
    ...Object.fromEntries(dash.categorias.map((c) => [`cat_${c}`, Number(s.consumo_por_categoria[c])])),
  }));
  // Cor segue a categoria (ordem fixa do cadastro); além de 3, o resto vira "Outras".
  const categoriasCor = dash.categorias.slice(0, 3);
  const temOutras = dash.categorias.length > 3;
  const serieCat = serie.map((s) => ({
    ...s,
    cat_Outras: temOutras ? dash.categorias.slice(3).reduce((acc, c) => acc + s[`cat_${c}`], 0) : 0,
  }));
  const catsPlot = [...categoriasCor.map((c, i) => ({ chave: `cat_${c}`, rotulo: c, cor: T.series[i] })), ...(temOutras ? [{ chave: "cat_Outras", rotulo: "Outras", cor: T.muda }] : [])];

  const top10 = dash.top10_consumo.map((t) => ({ ...t, valor: Number(t.valor) }));
  const abc = dash.abc.itens.map((a) => ({ ...a, pct: Number(a.pct), pct_acumulado: Number(a.pct_acumulado), valor: Number(a.valor) }));
  const abcContagem = ["A", "B", "C"].map((c) => ({ classe: c, n: abc.filter((a) => a.classe === c).length, valor: abc.filter((a) => a.classe === c).reduce((s, a) => s + a.valor, 0) }));
  const grid2 = { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 };
  const ultimo = serie[serie.length - 1];

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        <Kpi C={C} rotulo="Valor em estoque" valor={formatBRL(kpis.valor_estoque)} detalhe={`Saldo final de ${dash.rotulo}`} />
        <Kpi C={C} rotulo="Entradas no período" valor={formatBRL(kpis.entradas)} detalhe="Soma das NFs de entrada" />
        <Kpi C={C} rotulo="Consumo do período" valor={formatBRL(kpis.consumo)} detalhe={<VariacaoConsumo C={C} kpis={kpis} />} />
        <Kpi C={C} rotulo="Abaixo do mínimo" valor={kpis.produtos_abaixo_minimo} detalhe="Produtos com saldo final < mínimo" alerta={kpis.produtos_abaixo_minimo > 0} Icone={AlertTriangle} />
        <Kpi C={C} rotulo="Rupturas no período" valor={kpis.produtos_com_ruptura} detalhe="Produtos que faltaram em algum momento" alerta={kpis.produtos_com_ruptura > 0} Icone={PackageX} />
      </div>

      <div style={grid2}>
        <CardGrafico C={C} titulo="Entradas × consumo por mês" subtitulo="R$ · últimos 12 meses"
          tabela={<TabelaDados C={C} linhas={serie} colunas={[
            { id: "rotulo", rotulo: "Mês" }, { id: "entradas", rotulo: "Entradas", alinhar: "right", formato: formatBRL }, { id: "consumo", rotulo: "Consumo", alinhar: "right", formato: formatBRL },
          ]} />}>
          <Legenda T={T} itens={[{ rotulo: "Entradas", cor: T.series[0] }, { rotulo: "Consumo", cor: T.series[1] }]} />
          <ResponsiveContainer height="88%">
            <BarChart data={serie} barGap={2} barCategoryGap="28%" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={T.grid} />
              <XAxis dataKey="rotulo" {...eixoProps(T)} />
              <YAxis tickFormatter={brlCompacto} width={62} {...eixoProps(T)} />
              <Tooltip cursor={{ fill: T.grid, opacity: 0.5 }} content={({ active, payload, label }) => active && payload?.length ? (
                <CaixaTooltip T={T} titulo={label} linhas={[
                  { rotulo: "Entradas", valor: formatBRL(payload[0].payload.entradas), cor: T.series[0] },
                  { rotulo: "Consumo", valor: formatBRL(payload[0].payload.consumo), cor: T.series[1] },
                ]} />) : null} />
              <Bar dataKey="entradas" fill={T.series[0]} radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
              <Bar dataKey="consumo" fill={T.series[1]} radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </CardGrafico>

        <CardGrafico C={C} titulo="Valor em estoque" subtitulo={`R$ no fim de cada mês · ${dash.rotulo}: ${formatBRL(ultimo?.valor_estoque)}`}
          tabela={<TabelaDados C={C} linhas={serie} colunas={[{ id: "rotulo", rotulo: "Mês" }, { id: "valor_estoque", rotulo: "Valor em estoque", alinhar: "right", formato: formatBRL }]} />}>
          <ResponsiveContainer>
            <LineChart data={serie} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={T.grid} />
              <XAxis dataKey="rotulo" {...eixoProps(T)} />
              <YAxis tickFormatter={brlCompacto} width={62} {...eixoProps(T)} />
              <Tooltip cursor={{ stroke: T.eixo }} content={({ active, payload, label }) => active && payload?.length ? (
                <CaixaTooltip T={T} titulo={label} linhas={[{ rotulo: "Valor em estoque", valor: formatBRL(payload[0].value), cor: T.series[0] }]} />) : null} />
              <Line type="linear" dataKey="valor_estoque" stroke={T.series[0]} strokeWidth={2} dot={{ r: 4, fill: T.series[0], stroke: T.superficie, strokeWidth: 2 }} activeDot={{ r: 6 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardGrafico>

        <CardGrafico C={C} titulo="Top 10 produtos por consumo" subtitulo={`R$ consumidos em ${dash.rotulo}`} altura={Math.max(200, top10.length * 28 + 30)}
          tabela={<TabelaDados C={C} linhas={top10} colunas={[
            { id: "produto_nome", rotulo: "Produto" }, { id: "categoria", rotulo: "Categoria" },
            { id: "quantidade", rotulo: "Qtd.", alinhar: "right", formato: formatQtd }, { id: "valor", rotulo: "Consumo", alinhar: "right", formato: formatBRL },
          ]} />}>
          {top10.length === 0 ? <div style={{ color: C.muted, fontSize: 13, padding: 24, textAlign: "center" }}>Sem consumo no período.</div> : (
            <ResponsiveContainer>
              <BarChart data={top10} layout="vertical" barCategoryGap={4} margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid horizontal={false} stroke={T.grid} />
                <XAxis type="number" tickFormatter={brlCompacto} {...eixoProps(T)} />
                <YAxis type="category" dataKey="produto_nome" width={isMobile ? 110 : 150} {...eixoProps(T)} tick={{ fill: T.tinta2, fontSize: 11 }} />
                <Tooltip cursor={{ fill: T.grid, opacity: 0.5 }} content={({ active, payload }) => active && payload?.length ? (
                  <CaixaTooltip T={T} titulo={payload[0].payload.produto_nome} linhas={[
                    { rotulo: "Consumo", valor: formatBRL(payload[0].payload.valor), cor: T.series[0] },
                    { rotulo: "Quantidade", valor: formatQtd(payload[0].payload.quantidade) },
                  ]} />) : null} />
                <Bar dataKey="valor" fill={T.series[0]} radius={[0, 4, 4, 0]} maxBarSize={18} isAnimationActive={false} style={{ cursor: "pointer" }}
                  onClick={(item) => item?.payload && navigate(`/estoque/kardex?produto=${item.payload.produto_id}`)} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardGrafico>

        <CardGrafico C={C} titulo="Consumo por categoria" subtitulo="R$ por mês"
          tabela={<TabelaDados C={C} linhas={serieCat} colunas={[{ id: "rotulo", rotulo: "Mês" }, ...catsPlot.map((c) => ({ id: c.chave, rotulo: c.rotulo, alinhar: "right", formato: formatBRL }))]} />}>
          <Legenda T={T} itens={catsPlot} />
          <ResponsiveContainer height="88%">
            <BarChart data={serieCat} barCategoryGap="28%" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={T.grid} />
              <XAxis dataKey="rotulo" {...eixoProps(T)} />
              <YAxis tickFormatter={brlCompacto} width={62} {...eixoProps(T)} />
              <Tooltip cursor={{ fill: T.grid, opacity: 0.5 }} content={({ active, payload, label }) => active && payload?.length ? (
                <CaixaTooltip T={T} titulo={label} linhas={[
                  ...catsPlot.map((c) => ({ rotulo: c.rotulo, valor: formatBRL(payload[0].payload[c.chave]), cor: c.cor })),
                  { rotulo: "Total", valor: formatBRL(payload[0].payload.consumo) },
                ]} />) : null} />
              {catsPlot.map((c, i) => (
                <Bar key={c.chave} dataKey={c.chave} stackId="cat" fill={c.cor} stroke={T.superficie} strokeWidth={2} maxBarSize={28}
                  radius={i === catsPlot.length - 1 ? [4, 4, 0, 0] : 0} isAnimationActive={false} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CardGrafico>
      </div>

      <div style={{ marginTop: 12 }}>
        <CardGrafico C={C} titulo="Curva ABC por valor consumido" altura={280}
          subtitulo={`${dash.abc.de} a ${dash.abc.ate} · A: ${abcContagem[0].n} produto(s) = ${formatBRL(abcContagem[0].valor)} · B: ${abcContagem[1].n} · C: ${abcContagem[2].n}`}
          tabela={<TabelaDados C={C} linhas={abc} colunas={[
            { id: "classe", rotulo: "Classe" }, { id: "produto_nome", rotulo: "Produto" }, { id: "valor", rotulo: "Consumo", alinhar: "right", formato: formatBRL },
            { id: "pct", rotulo: "% do total", alinhar: "right", formato: (v) => `${String(v).replace(".", ",")}%` },
            { id: "pct_acumulado", rotulo: "% acumulado", alinhar: "right", formato: (v) => `${String(v).replace(".", ",")}%` },
          ]} />}>
          {abc.length === 0 ? <div style={{ color: C.muted, fontSize: 13, padding: 24, textAlign: "center" }}>Sem consumo na janela.</div> : (
            <>
              <Legenda T={T} itens={[
                { rotulo: "Classe A (até 80% do consumo)", cor: T.abc.A }, { rotulo: "Classe B (80–95%)", cor: T.abc.B },
                { rotulo: "Classe C (últimos 5%)", cor: T.abc.C }, { rotulo: "% acumulado", cor: T.tinta2, linha: true },
              ]} />
              <ResponsiveContainer height="85%">
                <ComposedChart data={abc} barCategoryGap={2} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={T.grid} />
                  <XAxis dataKey="produto_nome" tick={abc.length > 12 ? false : { fill: T.muda, fontSize: 10 }} axisLine={{ stroke: T.eixo }} tickLine={false} interval={0} />
                  <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={42} {...eixoProps(T)} />
                  <ReferenceLine y={80} stroke={T.eixo} />
                  <ReferenceLine y={95} stroke={T.eixo} />
                  <Tooltip cursor={{ fill: T.grid, opacity: 0.5 }} content={({ active, payload }) => active && payload?.length ? (
                    <CaixaTooltip T={T} titulo={`${payload[0].payload.produto_nome} · classe ${payload[0].payload.classe}`} linhas={[
                      { rotulo: "Consumo", valor: formatBRL(payload[0].payload.valor), cor: T.abc[payload[0].payload.classe] },
                      { rotulo: "% do total", valor: `${String(payload[0].payload.pct).replace(".", ",")}%` },
                      { rotulo: "% acumulado", valor: `${String(payload[0].payload.pct_acumulado).replace(".", ",")}%` },
                    ]} />) : null} />
                  <Bar dataKey="pct" radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false}>
                    {abc.map((a) => <Cell key={a.produto_id} fill={T.abc[a.classe]} />)}
                  </Bar>
                  <Line type="linear" dataKey="pct_acumulado" stroke={T.tinta2} strokeWidth={2} dot={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </>
          )}
        </CardGrafico>
      </div>

      <div style={{ marginTop: 12 }}>
        <GraficoCustoUnitario C={C} T={T} />
      </div>
    </>
  );
}

export default function DashboardEstoqueView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const T = temaGrafico(isLight);
  const isMobile = useIsMobile();
  const { contexto, erro: erroContexto } = useContextoEstoque();
  const hoje = new Date();
  const [filtro, setFiltro] = useState({ ano: hoje.getFullYear(), mes: hoje.getMonth() + 1 });
  const [aba, setAba] = useState("geral");
  const [dash, setDash] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!contexto?.abertura_concluida) return;
    setCarregando(true);
    getEstoqueDashboard(filtro.ano, filtro.mes).then(setDash).catch((e) => setErro(e.message)).finally(() => setCarregando(false));
  }, [filtro, contexto]);

  // Meses disponíveis: da abertura até o mês atual.
  const meses = useMemo(() => {
    if (!contexto?.data_abertura) return [];
    const [a0, m0] = contexto.data_abertura.split("-").map(Number);
    const lista = [];
    let a = hoje.getFullYear(), m = hoje.getMonth() + 1;
    while (a > a0 || (a === a0 && m >= m0)) {
      lista.push({ ano: a, mes: m });
      if (m === 1) { a -= 1; m = 12; } else m -= 1;
    }
    return lista;
  }, [contexto]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <EstoqueLayout titulo="Dashboard e Insights" isLight={isLight} setIsLight={setIsLight}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro || erroContexto}</AvisoErro>
      {!contexto ? null : !contexto.abertura_concluida ? <AvisoSemAbertura C={C} /> : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12, marginBottom: 12 }}>
            <div style={{ flex: "0 1 200px" }}>
              <Campo label="Período" C={C}>
                <select value={`${filtro.ano}-${filtro.mes}`} onChange={(e) => { const [a, m] = e.target.value.split("-").map(Number); setFiltro({ ano: a, mes: m }); }} style={inputStyle(C)}>
                  {meses.map((p) => <option key={`${p.ano}-${p.mes}`} value={`${p.ano}-${p.mes}`}>{MESES[p.mes - 1]} {p.ano}</option>)}
                </select>
              </Campo>
            </div>
            <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${C.border}`, marginLeft: isMobile ? 0 : "auto" }}>
              {[["geral", "Visão geral"], ["insights", `Insights${dash ? ` (${dash.kpis.insights})` : ""}`]].map(([id, rotulo]) => (
                <button key={id} onClick={() => setAba(id)} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", borderBottom: `2px solid ${aba === id ? C.brand : "transparent"}`, color: aba === id ? C.brand : C.muted, fontWeight: aba === id ? 700 : 500, fontSize: 14, padding: "10px 12px", minHeight: 44, cursor: "pointer", fontFamily: "inherit" }}>
                  {id === "insights" && <Lightbulb size={15} />} {rotulo}
                </button>
              ))}
            </div>
          </div>
          {/* Mantém o render anterior esmaecido durante o refetch (sem "piscar" esqueleto). */}
          <div style={{ opacity: carregando ? 0.55 : 1, transition: "opacity 0.15s" }}>
            {aba === "geral"
              ? dash?.disponivel && <VisaoGeral C={C} T={T} dash={dash} isMobile={isMobile} />
              : <InsightsEstoque C={C} ano={filtro.ano} mes={filtro.mes} isMobile={isMobile} />}
          </div>
        </>
      )}
    </EstoqueLayout>
  );
}
